import Entity from "../../core/entity/Entity";
import { rNormal } from "../../core/util/Random";
import { V } from "../../core/Vector";
import { LevelEntity } from "../LevelEntity";

const LASER_BASE_FREQUENCY = 200; // Hz
const LASER_FREQUENCY_SWEEP = 600; // Hz sweep range
const LASER_DURATION = 0.05; // seconds
const LASER_VOLUME = 0.15; // volume level
const LOWPASS_START_FREQUENCY = 1800;
const LOWPASS_END_FREQUENCY = 400;

export default class TurretSoundGenerator
  extends LevelEntity
  implements Entity
{
  tags = ["turretSoundGenerator"];

  /**
   * Override getPosition to return parent's position since sound generators don't have their own position
   */
  getPosition() {
    if (this.parent && "getPosition" in this.parent) {
      return (this.parent as any).getPosition();
    }
    return V(0, 0);
  }

  /**
   * Called by the turret when it fires a shot
   */
  onShoot() {
    if (!this.game?.audio || !this.game?.masterGain) {
      return;
    }

    this.synthesizeLaserSound();
  }

  private synthesizeLaserSound() {
    if (!this.game?.audio || !this.game?.masterGain) {
      return;
    }

    const ctx = this.game.audio;
    const now = ctx.currentTime;

    // Create oscillator for the main laser tone
    const oscillator = ctx.createOscillator();
    oscillator.type = "square"; // Classic arcade sound

    // Create gain node for volume control
    const gainNode = ctx.createGain();

    // Create filter for tonal shaping
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(LOWPASS_START_FREQUENCY, now);
    filter.Q.setValueAtTime(1, now);

    // Connect audio graph: oscillator -> filter -> gain -> master
    oscillator.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(this.game!.masterGain);

    // Frequency sweep: start high, sweep down quickly
    const startFreq =
      (LASER_BASE_FREQUENCY + LASER_FREQUENCY_SWEEP) * rNormal(1, 0.1);
    const endFreq = LASER_BASE_FREQUENCY * rNormal(1, 0.1);

    oscillator.frequency.setValueAtTime(startFreq * rNormal(1, 0.1), now);
    oscillator.frequency.exponentialRampToValueAtTime(
      endFreq * rNormal(1, 0.1),
      now + LASER_DURATION * 0.7,
    );

    // Volume envelope: quick attack, moderate decay
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(LASER_VOLUME, now + 0.01); // 10ms attack
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + LASER_DURATION); // decay to near zero

    // Filter sweep for extra character
    filter.frequency.exponentialRampToValueAtTime(
      LOWPASS_END_FREQUENCY,
      now + LASER_DURATION,
    );

    // Start and stop the oscillator
    oscillator.start(now);
    oscillator.stop(now + LASER_DURATION);

    // Clean up nodes after sound finishes
    oscillator.addEventListener("ended", () => {
      oscillator.disconnect();
      gainNode.disconnect();
      filter.disconnect();
    });
  }
}
